import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Customers from "@/pages/Customers";
import Parties from "@/pages/Parties";
import POS from "@/pages/POS";
import Bills from "@/pages/Bills";
import BillDetail from "@/pages/BillDetail";
import Returns from "@/pages/Returns";
import Users from "@/pages/Users";
import Branches from "@/pages/Branches";
// POS TERMINAL FEATURE — ON HOLD, DO NOT DELETE
// import POSTerminals from "@/pages/POSTerminals";
import Expenses from "@/pages/Expenses";
import PurchaseBills from "@/pages/PurchaseBills";
import PurchaseReturns from "@/pages/PurchaseReturns";
import ImportJobs from "@/pages/ImportJobs";
import Loyalty from "@/pages/Loyalty";
import Campaigns from "@/pages/Campaigns";
import Approvals from "@/pages/Approvals";
import BulkImport from "@/pages/BulkImport";
import BarcodeLabels from "@/pages/BarcodeLabels";
import BI from "@/pages/BI";
import Templates from "@/pages/Templates";
import Triggers from "@/pages/Triggers";
import Reports from "@/pages/Reports";
import AuditLogs from "@/pages/AuditLogs";
import Staff from "@/pages/Staff";
import OrgSettings from "@/pages/OrgSettings";
import BillBuilder from "@/pages/BillBuilder";
import LabelBuilder from "@/pages/LabelBuilder";
import Credits from "@/pages/Credits";
import PartyLedger from "@/pages/PartyLedger";

function RoleAwareHome() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Dashboard />;
  // Admin / cashier → direct to operations screen
  return <Navigate to="/bills" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<RoleAwareHome />} />
            {/* POS billing screen — re-enabled. POS terminals are now implicit via Cashier user */}
            <Route path="pos" element={<POS />} />
            <Route path="bills" element={<Bills />} />
            <Route path="bills/:id" element={<BillDetail />} />
            <Route path="returns" element={<Returns />} />
            <Route path="products" element={<ProtectedRoute roles={["super_admin", "admin"]}><Products /></ProtectedRoute>} />
            <Route path="customers" element={<Customers />} />
            <Route path="parties" element={<ProtectedRoute roles={["super_admin", "admin"]}><Parties /></ProtectedRoute>} />
            <Route path="purchase-bills" element={<ProtectedRoute roles={["super_admin", "admin"]}><PurchaseBills /></ProtectedRoute>} />
            <Route path="purchase-returns" element={<ProtectedRoute roles={["super_admin", "admin"]}><PurchaseReturns /></ProtectedRoute>} />
            <Route path="expenses" element={<ProtectedRoute permission="expense_manage"><Expenses /></ProtectedRoute>} />
            <Route path="loyalty" element={<ProtectedRoute roles={["super_admin"]}><Loyalty /></ProtectedRoute>} />
            <Route path="campaigns" element={<ProtectedRoute permission="whatsapp_campaign_create"><Campaigns /></ProtectedRoute>} />
            <Route path="approvals" element={<ProtectedRoute roles={["super_admin"]}><Approvals /></ProtectedRoute>} />
            <Route path="bulk-import" element={<ProtectedRoute roles={["super_admin"]}><BulkImport /></ProtectedRoute>} />
            <Route path="import-jobs" element={<ProtectedRoute roles={["super_admin", "admin"]}><ImportJobs /></ProtectedRoute>} />
            <Route path="barcode-labels" element={<ProtectedRoute permission="barcode_print"><BarcodeLabels /></ProtectedRoute>} />
            {/* POS TERMINAL FEATURE — ON HOLD, DO NOT DELETE */}
            {/* <Route path="pos-terminals" element={<ProtectedRoute permission="pos_create"><POSTerminals /></ProtectedRoute>} /> */}
            <Route path="branches" element={<ProtectedRoute roles={["super_admin"]}><Branches /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute roles={["super_admin"]}><Users /></ProtectedRoute>} />
            <Route path="bi" element={<ProtectedRoute roles={["super_admin"]}><BI /></ProtectedRoute>} />
            <Route path="templates" element={<ProtectedRoute roles={["super_admin", "admin"]}><Templates /></ProtectedRoute>} />
            <Route path="triggers" element={<ProtectedRoute roles={["super_admin"]}><Triggers /></ProtectedRoute>} />
            <Route path="reports" element={<ProtectedRoute roles={["super_admin"]}><Reports /></ProtectedRoute>} />
            <Route path="audit-logs" element={<ProtectedRoute roles={["super_admin"]}><AuditLogs /></ProtectedRoute>} />
            <Route path="staff" element={<ProtectedRoute roles={["super_admin", "admin"]}><Staff /></ProtectedRoute>} />
            <Route path="credits" element={<ProtectedRoute roles={["super_admin", "admin"]}><Credits /></ProtectedRoute>} />
            <Route path="parties/:partyId/ledger" element={<ProtectedRoute roles={["super_admin", "admin"]}><PartyLedger /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute roles={["super_admin"]}><OrgSettings /></ProtectedRoute>} />
            <Route path="settings/bill-builder" element={<ProtectedRoute roles={["super_admin"]}><BillBuilder /></ProtectedRoute>} />
            <Route path="settings/label-builder" element={<ProtectedRoute roles={["super_admin"]}><LabelBuilder /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
